import React, { useState } from 'react';
import { Calculator, X, Users, DollarSign } from 'lucide-react';

const ExpenseCalculator = ({ isOpen, onClose }) => {
  const [amount, setAmount] = useState('');
  const [people, setPeople] = useState(2);
  const [tipPercentage, setTipPercentage] = useState(0);

  const calculateSplit = () => {
    const baseAmount = parseFloat(amount) || 0;
    const tip = baseAmount * (Math.max(0, tipPercentage) / 100); // Ensure tip is not negative
    const total = baseAmount + tip;
    const perPerson = total / people;
    
    return {
      baseAmount,
      tip,
      total,
      perPerson
    };
  };

  const { baseAmount, tip, total, perPerson } = calculateSplit();

  // Handle amount input - prevent negative values
  const handleAmountChange = (e) => {
    const value = e.target.value;
    // Allow empty string or positive numbers only
    if (value === '' || (parseFloat(value) >= 0 && !isNaN(parseFloat(value)))) {
      setAmount(value);
    }
  };

  // Handle tip percentage - prevent negative values
  const handleTipChange = (e) => {
    const value = parseFloat(e.target.value);
    if (isNaN(value)) {
      setTipPercentage(0);
    } else {
      setTipPercentage(Math.max(0, Math.min(100, value))); // Between 0 and 100
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-full max-w-md shadow-lg rounded-md bg-white">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium text-gray-900 flex items-center">
            <Calculator className="h-5 w-5 mr-2 text-teal-600" />
            Expense Calculator
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-6">
          {/* Amount Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Total Amount (₹)
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={amount}
              onChange={handleAmountChange}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
              placeholder="Enter amount (must be positive)"
            />
            {amount && parseFloat(amount) < 0 && (
              <p className="text-red-500 text-xs mt-1">Amount cannot be negative</p>
            )}
          </div>

          {/* Number of People */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Users className="inline h-4 w-4 mr-1" />
              Number of People
            </label>
            <div className="flex items-center justify-center space-x-4">
              <button
                onClick={() => setPeople(Math.max(1, people - 1))}
                className="w-10 h-10 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center font-semibold transition-colors disabled:opacity-50"
                disabled={people <= 1}
              >
                -
              </button>
              <span className="text-2xl font-bold w-12 text-center">{people}</span>
              <button
                onClick={() => setPeople(people + 1)}
                className="w-10 h-10 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center font-semibold transition-colors"
              >
                +
              </button>
            </div>
            <p className="text-xs text-gray-500 text-center mt-1">Minimum 1 person</p>
          </div>

          {/* Tip Percentage */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tip Percentage
            </label>
            <div className="grid grid-cols-4 gap-2 mb-3">
              {[0, 10, 15, 20].map(tip => (
                <button
                  key={tip}
                  onClick={() => setTipPercentage(tip)}
                  className={`py-2 px-3 rounded-md text-sm font-medium transition-colors ${
                    tipPercentage === tip
                      ? 'bg-teal-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {tip}%
                </button>
              ))}
            </div>
            <input
              type="number"
              min="0"
              max="100"
              step="0.1"
              value={tipPercentage}
              onChange={handleTipChange}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
              placeholder="Custom tip % (0-100)"
            />
            <p className="text-xs text-gray-500 mt-1">Tip must be between 0% and 100%</p>
          </div>

          {/* Results */}
          <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-lg p-4 space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Subtotal:</span>
              <span className="font-semibold">₹{baseAmount.toFixed(2)}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Tip ({Math.max(0, tipPercentage).toFixed(1)}%):</span>
              <span className="font-semibold">₹{tip.toFixed(2)}</span>
            </div>
            <div className="border-t pt-3 flex justify-between">
              <span className="font-semibold text-gray-900">Total:</span>
              <span className="font-bold text-xl text-gray-900">₹{total.toFixed(2)}</span>
            </div>
            <div className="bg-teal-500 text-white -mx-4 -mb-4 p-4 rounded-b-lg">
              <div className="flex justify-between items-center">
                <span className="font-semibold flex items-center">
                  <DollarSign className="h-5 w-5 mr-1" />
                  Per Person:
                </span>
                <span className="font-bold text-2xl">₹{perPerson.toFixed(2)}</span>
              </div>
            </div>
          </div>

          <div className="flex space-x-3">
            <button
              onClick={() => {
                setAmount('');
                setPeople(2);
                setTipPercentage(0);
              }}
              className="flex-1 py-2 px-4 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Clear
            </button>
            <button
              onClick={onClose}
              className="flex-1 py-2 px-4 bg-teal-600 text-white rounded-md hover:bg-teal-700 transition-colors"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExpenseCalculator;

